"use client";

import { SuperLink as Link } from "@/components/common/super-link";
import { cn } from "@/lib/utils";
import { ArrowUpRightIcon } from "lucide-react";
import { getAssetImage } from "@/service/image/asset";
import { PictureAsset } from "@/components/common/picture-asset";
import { ReactNode, useContext } from "react";
import { logEvent } from "@/service/logs/log";
import { ResourceContext } from "@/contexts/resource-context";
export const CardToolsSpecial = ({
  title,
  subtitle,
  link,
  categoryName,
  categoryLink,
  onClick = () => {},
  onClickCategory = () => {},
  bgId,
  className,
  icon,
  textClassName,
}: {
  title: string;
  subtitle?: string;
  link: string;
  categoryName?: string;
  categoryLink?: string;
  onClick?: () => void;
  onClickCategory?: () => void;
  className?: string;
  bgId: number;
  icon?: ReactNode;
  textClassName?: string;
}) => {
  const { logView } = useContext(ResourceContext);

  const handleClick = () => {
    logEvent("tools_special_card_click", logView, {
      tools_special_card_title: title,
      tools_special_card_link: link,
      tools_special_card_category: categoryName,
    });
    onClick();
  };

  const handleClickCategory = () => {
    logEvent("tools_special_card_category_click", logView, {
      tools_special_card_category_name: categoryName,
      tools_special_card_category_link: categoryLink,
      tools_special_card_title: title,
    });
    onClickCategory();
  };

  const Wrapper = ({ children }: { children: ReactNode }) => (
    <div
      className={cn(
        `group relative z-0 max-h-[200px] min-h-[148px] overflow-hidden rounded-xl p-6 transition-all hover:scale-99 hover:cursor-pointer @max-[640px]:my-1`,
        !categoryLink && "flex items-center",
        className,
      )}
    >
      <div
        className={
          "absolute inset-0 h-full w-full transition-all group-hover:brightness-95"
        }
      >
        <PictureAsset
          source={getAssetImage(`/tool-special-cards/g${bgId}`)}
          ext={"png"}
          alt="tło"
          className={"h-full w-full"}
        />
      </div>
      <a
        href={link}
        onClick={handleClick}
        className={cn("absolute-link z-[100]!")}
      />
      {children}
    </div>
  );

  if (!categoryLink) {
    return (
      <Wrapper>
        <div
          className={cn(
            "relative z-2 flex h-full w-full flex-col items-center justify-center",
            !icon && "items-start",
          )}
        >
          {icon && (
            <div className="mb-3 flex items-center justify-center">{icon}</div>
          )}
          <h3
            className={cn(
              "text-center text-base leading-normal font-semibold text-white transition-all",
              textClassName,
            )}
          >
            {title}
          </h3>
        </div>
      </Wrapper>
    );
  }
  return (
    <Wrapper>
      <Link href={link as string} className={`relative z-1 mb-6`}>
        <div
          className={cn("flex flex-col items-center", !icon && "items-start")}
        >
          {icon && (
            <div className="mb-2 flex items-center justify-center">{icon}</div>
          )}
          <h3
            className={cn(
              "line-clamp-3 max-w-[95%] text-center text-base leading-normal font-semibold text-white transition-all",
              textClassName,
              !icon && "text-left",
            )}
          >
            {title}
            {subtitle && (
              <span className={`mt-1 block text-sm font-normal text-zinc-100`}>
                {subtitle}
              </span>
            )}
          </h3>
        </div>
      </Link>

      <div
        className={
          "absolute bottom-6 left-6 z-10 mt-4 flex w-[calc(100%-48px)] items-center justify-between"
        }
      >
        <Link
          href={categoryLink as string}
          onClick={(e) => {
            e.stopPropagation();
            handleClickCategory();
          }}
        >
          <div
            className={`text-sm font-medium text-white transition-all hover:text-zinc-200`}
          >
            {categoryName}
          </div>
        </Link>
        <div
          className={cn(
            "text-white transition-all group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:scale-125",
          )}
        >
          <ArrowUpRightIcon size={"16"} />
        </div>
      </div>
    </Wrapper>
  );
};
